import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, BookOpen, Users, Calendar } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/payments/checkout/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentStatus(data);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-20">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Payment Successful! 🎉
          </h1>
          <p className="text-xl text-gray-600">
            Welcome to Obed Ansu Academy
          </p>
        </div>

        {/* Payment Details */}
        {paymentStatus && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Payment Details</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                <p className="text-2xl font-bold text-gray-900">
                  £{(paymentStatus.amount_total / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <p className="text-lg font-semibold text-green-600">
                  {paymentStatus.payment_status === 'paid' ? 'Paid' : paymentStatus.payment_status}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">What's Next?</h2>
          
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Your Resources</h3>
                <p className="text-gray-600 mb-3">
                  Explore our library of 71 free ebooks and choose your 2 complimentary courses.
                </p>
                <button
                  onClick={() => navigate('/library')}
                  className="text-purple-600 hover:text-purple-700 font-medium flex items-center"
                >
                  Visit Library <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Join the Community</h3>
                <p className="text-gray-600 mb-3">
                  Connect with fellow mentees in our vibrant community channels.
                </p>
                <button
                  onClick={() => navigate('/community')}
                  className="text-orange-600 hover:text-orange-700 font-medium flex items-center"
                >
                  Enter Community <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Book Your Monthly Session</h3>
                <p className="text-gray-600 mb-3">
                  Schedule your monthly Q&A session with your mentor via Zoom.
                </p>
                <button
                  onClick={() => navigate('/appointments')}
                  className="text-green-600 hover:text-green-700 font-medium flex items-center"
                >
                  Book Session <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-purple-600 to-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-purple-700 hover:to-orange-600 transition-all duration-200 inline-flex items-center"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>

        {/* Confirmation Email Note */}
        <p className="text-center text-sm text-gray-500 mt-6">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
